import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useStore } from "../store";
import { toast } from "sonner";
import { WHATSAPP_LINK, SUPPORT_EMAIL, INSTAGRAM_LINK } from "../Layout";
import { MessageCircle, Mail, Phone, MapPin, Instagram, Package, CheckCircle2 } from "lucide-react";

// ============ Static / Legal Pages ============
function PolicyShell({ overline, title, children, testId }) {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid={testId}>
      <div className="overline mb-3" style={{color:"var(--secondary)"}}>{overline}</div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mb-8" style={{color:"var(--primary)"}}>{title}</h1>
      <div className="prose max-w-none text-sm leading-relaxed opacity-85 space-y-4">{children}</div>
      <div className="mt-14 pt-6 border-t text-xs opacity-60" style={{borderColor:"var(--border)"}}>
        Last updated: January 2026. For queries: <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> · +91-9911298518
      </div>
    </div>
  );
}
const H = ({children}) => <h2 className="font-serif text-2xl mt-8 mb-2" style={{color:"var(--primary)"}}>{children}</h2>;

export function PrivacyPage() {
  return (
    <PolicyShell overline="Legal" title="Privacy Policy" testId="page-privacy">
      <p>Eloire Jewels ("we", "us", "our") respects your privacy and is committed to protecting your personal data.</p>
      <H>Information We Collect</H>
      <p>Name, email, phone, shipping addresses, order history, and payment metadata (Razorpay handles all card/UPI data; we never store payment credentials). We also collect usage analytics to improve your shopping experience.</p>
      <H>How We Use Your Data</H>
      <p>To process orders, send transactional emails (order updates, returns), respond to enquiries, prevent fraud, and personalise your experience. We do not sell your data. Marketing emails are opt-in only via the newsletter subscription.</p>
      <H>Data Sharing</H>
      <p>We share order details with our courier partners (for delivery), Razorpay (payments), and Zoho Mail (transactional email). All partners are contractually bound to data protection.</p>
      <H>Cookies</H>
      <p>We use essential cookies for authentication and cart persistence. Analytics cookies help us improve — you may disable them in your browser.</p>
      <H>Your Rights</H>
      <p>You may request access, correction, or deletion of your data at any time by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>. We respond within 7 business days.</p>
      <H>Retention</H>
      <p>Order records are retained for 7 years for tax compliance under Indian law. Personal data is deleted upon request unless required by law.</p>
    </PolicyShell>
  );
}

export function ShippingPage() {
  return (
    <PolicyShell overline="Policies" title="Shipping Policy" testId="page-shipping">
      <H>Shipping Charges</H>
      <p>Free shipping across India on all orders above ₹2000. Orders below ₹2000 attract a flat ₹99 shipping fee. Cash on Delivery (COD) available pan-India.</p>
      <H>Delivery Time</H>
      <p>Metro cities: 3-5 business days · Non-metro: 5-7 business days · Remote areas: 7-10 business days. All timelines exclude processing time (1-2 business days).</p>
      <H>Order Tracking</H>
      <p>You'll receive a tracking link via email once your order ships. You can also log in to your account and visit the <Link to="/account" className="underline">My Orders</Link> section.</p>
      <H>International Shipping</H>
      <p>We currently ship only within India. For international enquiries, please <Link to="/contact" className="underline">contact us</Link>.</p>
      <H>Undelivered / RTO</H>
      <p>If a parcel is returned undelivered (RTO) due to incorrect address or unreachable phone, the customer is liable for re-shipping charges. Please ensure your address and phone are correct at checkout.</p>
    </PolicyShell>
  );
}

export function ReturnsPolicyPage() {
  return (
    <PolicyShell overline="Policies" title="Return & Refund Policy" testId="page-returns-policy">
      <H>7-Day Return Window</H>
      <p>You may request a return or exchange within <b>7 days of delivery</b>. Requests beyond this window are not accepted.</p>
      <H>Eligibility</H>
      <ul className="list-disc pl-6 space-y-1">
        <li>Product must be unused, unworn, and in its original condition with all tags and packaging intact.</li>
        <li>Original invoice must be included.</li>
        <li>Damaged, broken, altered, engraved, or tampered products are <b>not eligible</b> for return.</li>
        <li>Customised or personalised pieces are non-returnable.</li>
      </ul>
      <H>How to Request</H>
      <p>Visit our <Link to="/returns" className="underline">Return & Exchange</Link> page and submit your request with photos/videos of the item. Our team reviews within 24-48 hours.</p>
      <H>Refund Timeline</H>
      <p>Once your return is approved and item is received & inspected, refunds are initiated to your original payment method within 3 business days. Reflection in your account takes 5-7 business days (bank-dependent).</p>
      <H>Exchange</H>
      <p>Same-value exchange for a different size or piece is available within the 7-day window, subject to stock. Price differences (if any) will be settled at the time of exchange.</p>
      <H>Contact</H>
      <p>For any return-related question, please WhatsApp us at <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="underline">+91-9911298518</a> or email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.</p>
    </PolicyShell>
  );
}

export function TermsPage() {
  return (
    <PolicyShell overline="Legal" title="Terms & Conditions" testId="page-terms">
      <H>Acceptance</H>
      <p>By using eloire.in, you agree to be bound by these Terms & Conditions and our Privacy Policy.</p>
      <H>Product Description</H>
      <p>We make every effort to display our products as accurately as possible. However, due to lighting, camera settings, and your device's display, colours and finish may vary slightly from the physical product.</p>
      <H>Pricing</H>
      <p>All prices are in Indian Rupees (INR) and inclusive of applicable taxes unless stated. We reserve the right to change prices at any time without notice.</p>
      <H>Order Acceptance</H>
      <p>An order confirmation is not a guarantee of order acceptance. We reserve the right to refuse or cancel an order at our discretion (e.g., stock unavailability, pricing errors, fraud detection).</p>
      <H>Intellectual Property</H>
      <p>All content — images, text, designs, logos — on eloire.in is the property of Eloire Jewels. Unauthorised reproduction, distribution, or use is prohibited.</p>
      <H>Limitation of Liability</H>
      <p>Eloire Jewels shall not be liable for any indirect, incidental, or consequential damages arising from use of our website or products. Our maximum liability is limited to the value of the order in question.</p>
      <H>Governing Law</H>
      <p>These terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts of Delhi.</p>
    </PolicyShell>
  );
}


// ============ About ============
export function AboutPage() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16" data-testid="page-about">
      <div className="grid md:grid-cols-2 gap-14 items-center mb-16">
        <div>
          <div className="overline mb-3" style={{color:"var(--secondary)"}}>Our Story</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter" style={{color:"var(--primary)"}}>Modern heirlooms, born in Delhi.</h1>
          <p className="mt-6 opacity-80 leading-relaxed">
            Eloire began with a simple idea — that jewellery should feel personal, be beautifully crafted, and last a lifetime. We work with a small circle of artisans across India to bring you pieces that carry the soul of tradition and the polish of modern design.
          </p>
          <p className="mt-4 opacity-80 leading-relaxed">
            Every Eloire piece is designed in Delhi and handcrafted with care. From delicate everyday studs to statement bridal haars — each design is thought through, drawn by hand, and made to be worn (and loved) for years.
          </p>
        </div>
        <img src="https://images.pexels.com/photos/30276932/pexels-photo-30276932.jpeg" alt="Eloire craftsmanship" className="w-full aspect-[4/5] object-cover"/>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {[
          {t:"Handcrafted", d:"Every piece is finished by hand by our master artisans."},
          {t:"Fair-price promise", d:"No middlemen. What you pay is honest craftsmanship value."},
          {t:"Built to last", d:"7-day easy returns, and lifetime after-care from our team."},
        ].map(x => (
          <div key={x.t} className="p-6 border" style={{borderColor:"var(--border)", background:"var(--muted)"}}>
            <div className="font-serif text-2xl" style={{color:"var(--primary)"}}>{x.t}</div>
            <div className="text-sm opacity-70 mt-2">{x.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-16 p-8 text-center" style={{background:"var(--primary)", color:"var(--primary-fg)"}}>
        <div className="font-serif text-3xl">Made in Delhi · Made for India</div>
        <div className="text-sm mt-3 opacity-80">Say hello: <a href={`mailto:${SUPPORT_EMAIL}`} style={{color:"var(--secondary)"}}>{SUPPORT_EMAIL}</a> · +91-9911298518</div>
      </div>
    </div>
  );
}


// ============ Contact ============
export function ContactPage() {
  const [form, setForm] = useState({ name:"", email:"", phone:"", subject:"General enquiry", message:"" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent ✨ We'll reply within 24 hours.");
      setForm({ name:"", email:"", phone:"", subject:"General enquiry", message:"" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="page-contact">
      <div className="overline mb-3" style={{color:"var(--secondary)"}}>Contact</div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mb-10" style={{color:"var(--primary)"}}>We'd love to hear from you.</h1>

      <div className="grid md:grid-cols-2 gap-14">
        <div>
          <p className="opacity-80 leading-relaxed">Have a question about a piece, an order, or a custom design idea? Our team responds within 24 hours (Mon–Sat).</p>
          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-center gap-3"><MapPin size={18} style={{color:"var(--secondary)"}}/> Delhi, India</div>
            <div className="flex items-center gap-3"><Phone size={18} style={{color:"var(--secondary)"}}/> <a href="tel:+919911298518">+91-9911298518</a></div>
            <div className="flex items-center gap-3"><MessageCircle size={18} style={{color:"var(--secondary)"}}/> <a href={WHATSAPP_LINK} target="_blank" rel="noopener">WhatsApp us — fastest response</a></div>
            <div className="flex items-center gap-3"><Mail size={18} style={{color:"var(--secondary)"}}/> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></div>
            <div className="flex items-center gap-3"><Instagram size={18} style={{color:"var(--secondary)"}}/> <a href={INSTAGRAM_LINK} target="_blank" rel="noopener">@eloire.in</a></div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="contact-name"/>
          <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="contact-email"/>
          <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="contact-phone"/>
          <select value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-4 py-3 border bg-white" style={{borderColor:"var(--border)"}} data-testid="contact-subject">
            <option>General enquiry</option><option>Order support</option><option>Custom design</option>
            <option>Return / Refund</option><option>Wholesale / Partnership</option><option>Other</option>
          </select>
          <textarea required rows={5} placeholder="Message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="contact-message"/>
          <button disabled={busy} className="btn-gold w-full" data-testid="contact-submit-btn">{busy ? "Sending..." : "Send Message"}</button>
        </form>
      </div>
    </div>
  );
}


// ============ FAQ ============
export function FaqPage() {
  const items = [
    {q:"Do you offer Cash on Delivery?", a:"Yes, COD is available across India."},
    {q:"What is your return policy?", a:"7-day easy returns from the date of delivery. See our Return & Refund Policy for full details."},
    {q:"How long does shipping take?", a:"Metro: 3-5 days · Non-metro: 5-7 days · Remote: 7-10 days. Free above ₹2000."},
    {q:"Are your products hallmarked?", a:"We work with certified artisans and use quality alloys, plated finishes, and semi-precious stones. Full material details are listed on each product."},
    {q:"Can I get something custom-made?", a:"Yes! WhatsApp us at +91-9911298518 or email support@eloire.in with your idea."},
    {q:"Do you have a physical store?", a:"We're an online-first brand based in Delhi. Follow us on Instagram @eloire.in for pop-ups and events."},
    {q:"How do I take care of my jewellery?", a:"Avoid perfume, water, and sweat. Store in a soft cloth pouch. Wipe gently after wear."},
  ];
  const [open, setOpen] = useState(0);
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="page-faq">
      <div className="overline mb-3" style={{color:"var(--secondary)"}}>Help Centre</div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mb-10" style={{color:"var(--primary)"}}>Frequently Asked</h1>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="border" style={{borderColor:"var(--border)"}}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex justify-between items-center px-5 py-4 text-left" data-testid={`faq-q-${i}`}>
              <span className="font-medium">{it.q}</span>
              <span style={{color:"var(--secondary)"}}>{open === i ? "−" : "+"}</span>
            </button>
            {open === i && <div className="px-5 pb-4 text-sm opacity-80 leading-relaxed">{it.a}</div>}
          </div>
        ))}
      </div>
      <div className="mt-14 p-6 text-center" style={{background:"var(--muted)"}}>
        <div className="text-sm opacity-80">Still have a question?</div>
        <div className="mt-2 flex justify-center gap-3">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener"><button className="btn-gold text-sm">WhatsApp Us</button></a>
          <Link to="/contact"><button className="btn-outline text-sm">Contact Form</button></Link>
        </div>
      </div>
    </div>
  );
}


// ============ Track ============
export function TrackOrderPage() {
  const [orderNo, setOrderNo] = useState("");
  const [result, setResult] = useState(null);
  const nav = useNavigate();
  const track = async () => {
    if (!orderNo.trim()) return;
    // Search orders by order_number using admin bypass? Simpler: guide to /account or use public GET /orders/{id}
    toast.info("Please sign in and visit 'My Account' to track your order.");
    nav("/account");
  };
  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="page-track">
      <div className="overline mb-3" style={{color:"var(--secondary)"}}>Order Tracking</div>
      <h1 className="font-serif text-5xl tracking-tighter mb-8" style={{color:"var(--primary)"}}>Track Your Order</h1>
      <p className="opacity-70 mb-6">Sign in to your account for real-time updates and delivery status.</p>
      <Link to="/account"><button className="btn-gold">Go to My Orders</button></Link>
    </div>
  );
}


// ============ Forgot / Reset Password ============
export function ForgotPasswordPage() {
  const [email, setEmail] = useState(""); const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setDone(true); }
    catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };
  if (done) {
    return <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="page-forgot-done">
      <CheckCircle2 size={40} className="mx-auto" style={{color:"var(--secondary)"}}/>
      <h1 className="font-serif text-3xl mt-4 tracking-tighter" style={{color:"var(--primary)"}}>Check your inbox</h1>
      <p className="opacity-70 mt-3 text-sm">If an account exists for that email, we've sent a reset link. Link expires in 30 minutes.</p>
      <Link to="/login"><button className="btn-outline mt-6 text-sm">Back to Sign In</button></Link>
    </div>;
  }
  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="page-forgot">
      <div className="overline text-center mb-3" style={{color:"var(--secondary)"}}>Password Reset</div>
      <h1 className="font-serif text-4xl tracking-tighter text-center mb-8" style={{color:"var(--primary)"}}>Forgot password?</h1>
      <form onSubmit={submit} className="space-y-4">
        <input required type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="forgot-email"/>
        <button disabled={busy} className="btn-gold w-full" data-testid="forgot-submit-btn">{busy ? "Sending..." : "Send Reset Link"}</button>
      </form>
      <div className="mt-6 text-center text-sm opacity-70"><Link to="/login" className="underline">← Back to sign in</Link></div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams(); const token = sp.get("token");
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try { await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password reset. Please sign in."); nav("/login"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="page-reset">
      <h1 className="font-serif text-4xl tracking-tighter text-center mb-8" style={{color:"var(--primary)"}}>Set new password</h1>
      <form onSubmit={submit} className="space-y-4">
        <input required type="password" placeholder="New password (min 6 chars)" value={pw} onChange={e => setPw(e.target.value)} className="w-full px-4 py-3 border" style={{borderColor:"var(--border)"}} data-testid="reset-password"/>
        <button disabled={busy} className="btn-gold w-full" data-testid="reset-submit-btn">{busy ? "..." : "Reset Password"}</button>
      </form>
    </div>
  );
}
